import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { LearnQA } from "@/components/learn/LearnQA";
import { gatedRobotsMeta } from "@/lib/seo-gating";
import { useState } from "react";
import { ChevronDown, ExternalLink } from "lucide-react";

import {
  EVIDENCE_BLURB,
  EVIDENCE_LABEL,
  GROUP_LABEL,
  THERAPIES,
  type Therapy,
} from "@/data/learn/therapies";

export const Route = createFileRoute("/learn/therapies/$slug")({
  // Therapy write-ups load when a therapy page opens.
  loader: async ({ params }) => {
    const { getTherapy } = await import("@/data/learn/therapies");
    const therapy = getTherapy(params.slug);
    if (!therapy) throw notFound();
    return { therapy };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Therapy not found — Recovery Trauma™" }, { name: "robots", content: "noindex" }],
      };
    }
    const t = loaderData.therapy;
    const title = `${t.abbrev ? `${t.abbrev} — ` : ""}${t.name} — Recovery Trauma™`;
    const description = t.tagline.slice(0, 155);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:url", content: `https://recoverytrauma.com/learn/therapies/${t.slug}` },
        ...gatedRobotsMeta(`/learn/therapies/${t.slug}`),
      ],
      links: [{ rel: "canonical", href: `https://recoverytrauma.com/learn/therapies/${t.slug}` }],
    };
  },
  component: TherapyPage,
  notFoundComponent: TherapyNotFound,
});

function TherapyNotFound() {
  return (
    <main className="mx-auto max-w-2xl px-4 py-16 text-center">
      <h1 className="font-serif text-3xl text-foreground">We couldn't find that approach</h1>
      <p className="mt-3 text-muted-foreground">
        It may have moved. Browse all approaches instead.
      </p>
      <Link
        to="/learn/therapies"
        className="mt-6 inline-block rounded-full border border-gold/60 px-5 py-2.5 text-sm font-semibold text-foreground"
      >
        Therapies &amp; Healing Approaches
      </Link>
    </main>
  );
}

type SectionDef = {
  id: string;
  title: string;
  key: keyof Therapy;
  as: "prose" | "list";
};

const SECTIONS: SectionDef[] = [
  { id: "what", title: "What is this therapy?", key: "whatIsIt", as: "prose" },
  { id: "how", title: "How does it work?", key: "howItWorks", as: "prose" },
  { id: "theory", title: "The theory behind it", key: "theory", as: "prose" },
  { id: "session", title: "What happens in a typical session?", key: "session", as: "prose" },
  { id: "techniques", title: "Techniques commonly used", key: "techniques", as: "list" },
  { id: "used-for", title: "What it's commonly used for", key: "usedFor", as: "list" },
  { id: "trauma", title: "Trauma & PTSD", key: "trauma", as: "prose" },
  { id: "cptsd", title: "Complex trauma / C-PTSD", key: "complexTrauma", as: "prose" },
  { id: "duration", title: "How long treatment might last", key: "duration", as: "list" },
  { id: "experience", title: "What people often experience", key: "experience", as: "prose" },
  { id: "benefits", title: "Potential benefits", key: "benefits", as: "list" },
  { id: "limitations", title: "Limitations & considerations", key: "limitations", as: "list" },
  { id: "evidence", title: "Evidence & research overview", key: "evidenceBase", as: "prose" },
  { id: "who", title: "Who is qualified to provide it?", key: "whoProvides", as: "prose" },
  { id: "self-help", title: "Self-help vs working with a professional", key: "selfHelp", as: "prose" },
  { id: "questions", title: "Questions you may want to ask a therapist", key: "questions", as: "list" },
];

function Accordion({
  id,
  title,
  items,
  as,
  defaultOpen,
}: {
  id: string;
  title: string;
  items: string[];
  as: "prose" | "list";
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  if (!items.length) return null;
  return (
    <section id={id} className="scroll-mt-24 rounded-2xl border border-border/70 bg-card/40">
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`sec-${id}`}
          onClick={() => setOpen((v) => !v)}
          className="flex min-h-14 w-full items-center gap-3 rounded-2xl px-4 py-4 text-start transition-colors hover:bg-card/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="min-w-0 flex-1 font-serif text-lg text-foreground">{title}</span>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
      </h2>
      {open ? (
        <div id={`sec-${id}`} className="px-4 pb-5">
          {as === "list" ? (
            <ul className="space-y-2">
              {items.map((item) => (
                <li key={item} className="flex gap-2 text-sm leading-relaxed text-foreground/80">
                  <span aria-hidden="true" className="text-gold">
                    •
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <p key={item} className="text-sm leading-relaxed text-foreground/80">
                  {item}
                </p>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}

function TherapyPage() {
  const { therapy } = Route.useLoaderData();
  const related = THERAPIES.filter((t) => t.slug !== therapy.slug && t.group === therapy.group).slice(0, 4);

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
        <Link to="/learn" className="hover:text-foreground hover:underline">
          Learn
        </Link>
        <span aria-hidden="true"> / </span>
        <Link to="/learn/therapies" className="hover:text-foreground hover:underline">
          Therapies
        </Link>
        <span aria-hidden="true"> / </span>
        <span className="text-foreground">{therapy.abbrev ?? therapy.name}</span>
      </nav>

      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/70">
            {therapy.badge}
          </span>
          <span className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground">
            {GROUP_LABEL[therapy.group]}
          </span>
        </div>
        <h1 className="mt-3 font-serif text-3xl leading-tight tracking-tight text-foreground md:text-4xl">
          {therapy.abbrev ? `${therapy.abbrev} — ` : ""}
          {therapy.name}
        </h1>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">{therapy.tagline}</p>
      </header>

      <section
        aria-label="Evidence level"
        className="mb-8 rounded-2xl border border-gold/40 bg-gold/5 p-4"
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
          Evidence level: {EVIDENCE_LABEL[therapy.evidence]}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-foreground/80">{therapy.evidenceSummary}</p>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {EVIDENCE_BLURB[therapy.evidence]}
        </p>
      </section>

      <div className="space-y-3">
        {SECTIONS.map((s, i) => (
          <Accordion
            key={s.id}
            id={s.id}
            title={s.title}
            items={therapy[s.key] as string[]}
            as={s.as}
            defaultOpen={i < 2}
          />
        ))}
      </div>

      {therapy.faq?.length ? (
        <LearnQA heading="Common questions" items={therapy.faq} />
      ) : null}

      <section aria-labelledby="rt-resources" className="mt-10">
        <h2 id="rt-resources" className="font-serif text-2xl text-foreground">
          Recovery Trauma™ resources that pair with this
        </h2>
        <ul className="-mx-4 mt-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>li]:w-[76%] [&>li]:shrink-0 [&>li]:snap-start sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 sm:[&>li]:w-auto">
          {therapy.resources.map((r) => (
            <li key={r.to}>
              <Link
                to={r.to}
                className="flex h-full flex-col rounded-2xl border border-border/70 bg-card/70 p-4 transition hover:border-gold/60"
              >
                <span className="font-serif text-base text-foreground">{r.label}</span>
                <span className="mt-1 text-sm leading-relaxed text-foreground/70">{r.blurb}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="refs" className="mt-10">
        <h2 id="refs" className="font-serif text-2xl text-foreground">
          References &amp; sources
        </h2>
        <ul className="mt-3 space-y-2">
          {therapy.references.map((r) => (
            <li key={r.url} className="text-sm leading-relaxed text-foreground/80">
              <a
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-start gap-1.5 text-gold underline-offset-4 hover:underline"
              >
                <span>{r.label}</span>
                <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              </a>
              <span className="text-muted-foreground"> — {r.source}</span>
            </li>
          ))}
        </ul>
      </section>

      {related.length ? (
        <section aria-labelledby="related" className="mt-10">
          <h2 id="related" className="font-serif text-2xl text-foreground">
            Related approaches
          </h2>
          <ul className="-mx-4 mt-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>li]:w-[76%] [&>li]:shrink-0 [&>li]:snap-start sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:[&>li]:w-auto">
            {related.map((t) => (
              <li key={t.slug}>
                <Link
                  to="/learn/therapies/$slug"
                  params={{ slug: t.slug }}
                  className="flex h-full flex-col rounded-2xl border border-border/70 bg-card/70 p-4 transition hover:border-gold/60"
                >
                  <span className="font-serif text-base text-foreground">
                    {t.abbrev ? `${t.abbrev} — ` : ""}
                    {t.name}
                  </span>
                  <span className="mt-1 text-sm leading-relaxed text-foreground/70">{t.tagline}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <p className="mt-12 rounded-2xl border border-border bg-muted/30 p-5 text-xs leading-relaxed text-muted-foreground">
        Recovery Trauma™ is a wellbeing and educational platform. It is not a crisis service,
        therapy, diagnosis, or medical treatment. This page is psychoeducation only — it cannot tell
        you whether this approach is right for you, and reading it is not the same as receiving
        psychotherapy from a qualified professional. Decisions about treatment belong with you and a
        registered clinician. If you need urgent support, please visit our{" "}
        <Link to="/crisis" className="text-gold underline-offset-2 hover:underline">
          Crisis Resources
        </Link>{" "}
        page.
      </p>
    </main>
  );
}
